import { useState } from 'react';
import { Download, Loader2, Share2, Check, Copy, PowerOff, Power } from 'lucide-react';
import { redeemCopyCode, generateThematiqueCopyCode, useThematiqueCopyCodes } from '../../hooks/useCategoryCopyCodes';

// Date « 20 juin 2026 14:32 » pour le suivi des copies par code.
function fmtRedeemDate(iso) {
    if (!iso) return '';
    try {
        return new Date(String(iso).replace(' ', 'T')).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

// Onglet "Gestion par code" : importer une copie (code CPY) + donner une copie éditable de toute la thématique.
export default function CodesTab({ thematiqueId }) {
    // Code de copie — import d'une catégorie partagée par un tiers
    const [importCode, setImportCode] = useState('');
    const [importBusy, setImportBusy] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importNotice, setImportNotice] = useState(null);

    // Codes de copie de TOUTE la thématique (don formateur→formateur) : génération + liste + révocation
    const [genBusy, setGenBusy] = useState(false);
    const [genError, setGenError] = useState(null);
    const [genCopiedId, setGenCopiedId] = useState(null);
    const [expandedCode, setExpandedCode] = useState(null);
    const { items: thematiqueCodes, load: loadThematiqueCodes, revoke: revokeThematiqueCode } = useThematiqueCopyCodes(thematiqueId);

    const handleImportCode = async () => {
        const c = importCode.trim().toUpperCase();
        if (!c || !thematiqueId) return;
        setImportBusy(true);
        setImportError(null);
        setImportNotice(null);
        try {
            const res = await redeemCopyCode(c, thematiqueId);
            setImportNotice(res.level === 'thematique'
                ? `Thématique importée — ${res.nb_categories_copied} catégorie(s), ${res.nb_sujets_copied} sujet(s)`
                : `Copie importée — ${res.nb_sujets_copied} sujet(s) ajouté(s)`);
            setImportCode('');
            // Rechargement complet après import : rafraîchit l'image/couleur de la
            // thématique (copie ISO), les catégories et le quota IA — sinon l'image
            // reste en cache (hat.image) jusqu'à un reload manuel.
            setTimeout(() => window.location.reload(), 1300);
        } catch (e) {
            setImportError(e.response?.data?.message || e.message);
        }
        setImportBusy(false);
    };

    const copyCode = (code, id) => {
        navigator.clipboard?.writeText(code).then(() => {
            setGenCopiedId(id);
            setTimeout(() => setGenCopiedId(null), 1500);
        }).catch(() => {});
    };
    const handleGenerateThematiqueCode = async () => {
        if (!thematiqueId || genBusy) return;
        setGenBusy(true);
        setGenError(null);
        try {
            const res = await generateThematiqueCopyCode(thematiqueId);
            await loadThematiqueCodes();
            if (res?.code) copyCode(res.code, res.id); // copie auto du nouveau code
        } catch (e) {
            setGenError(e.response?.data?.message || e.message);
        }
        setGenBusy(false);
    };
    const handleRevokeThematiqueCode = async (id, isActive) => {
        try { await revokeThematiqueCode(id, isActive); }
        catch (e) { setGenError(e.response?.data?.message || e.message); }
    };

    return (
        <div style={{ marginTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Download size={14} style={{ color: 'var(--primary, #8B5CF6)' }} />
                <strong style={{ fontSize: '0.92em' }}>Importer depuis un code de copie</strong>
            </div>
            <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                Code fourni par un tiers (format <code>CPY-XXXX-XXXX</code>) — d'une <strong>catégorie</strong>
                &nbsp;ou d'une <strong>thématique entière</strong>. Le contenu est ajouté ici comme de nouveaux
                sujets (catégories créées à neuf) ; l'origine « Copie » et sa provenance restent visibles.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <input
                    type="text"
                    placeholder="CPY-XXXX-XXXX"
                    value={importCode}
                    onChange={(e) => setImportCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleImportCode()}
                    disabled={importBusy}
                    maxLength={24}
                    style={{ flex: '1 1 160px', minWidth: 0 }}
                />
                <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={handleImportCode}
                    disabled={importBusy || !importCode.trim()}
                >
                    {importBusy
                        ? <><Loader2 size={14} className="spin" /> Import…</>
                        : <><Download size={14} /> Importer</>}
                </button>
            </div>
            {importError && (
                <div className="sem-notice sem-notice--error" style={{ marginTop: 6 }}>{importError}</div>
            )}
            {importNotice && (
                <div className="sem-notice sem-notice--info" style={{ marginTop: 6 }}>{importNotice}</div>
            )}

            {/* Côté propriétaire : donner une copie éditable de TOUTE la thématique */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--card-border, #e5e7eb)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Share2 size={14} style={{ color: 'var(--primary, #8B5CF6)' }} />
                    <strong style={{ fontSize: '0.92em' }}>Donner une copie de toute la thématique</strong>
                </div>
                <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                    Génère un code à transmettre : le destinataire (abonnement perso actif) recopie toute
                    la thématique chez lui, <strong>éditable</strong>. Idéal pour passer une formation toute faite.
                    Le code est réutilisable ; tu peux le révoquer quand tu veux (les copies déjà faites restent).
                </p>
                <button type="button" className="btn btn-sm btn-secondary" onClick={handleGenerateThematiqueCode} disabled={genBusy}>
                    {genBusy
                        ? <><Loader2 size={14} className="spin" /> Génération…</>
                        : <><Share2 size={14} /> Générer un code de copie</>}
                </button>
                {genError && (
                    <div className="sem-notice sem-notice--error" style={{ marginTop: 6 }}>{genError}</div>
                )}
                {thematiqueCodes.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {thematiqueCodes.map((c) => (
                            <div key={c.id} style={{
                                border: '1px solid var(--border)', borderRadius: 6,
                                background: c.is_active ? 'var(--surface, #fff)' : 'var(--bg-subtle, #f8fafc)',
                                opacity: c.is_active ? 1 : 0.6,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
                                    <code style={{ flex: 1, fontSize: '0.88em', fontWeight: 600, letterSpacing: 0.5 }}>{c.code}</code>
                                    <button
                                        type="button"
                                        onClick={() => c.nb_redemptions > 0 && setExpandedCode(expandedCode === c.id ? null : c.id)}
                                        style={{
                                            background: 'none', border: 'none', padding: 0, fontSize: '0.76em',
                                            color: 'var(--text-muted)', cursor: c.nb_redemptions > 0 ? 'pointer' : 'default',
                                            textDecoration: c.nb_redemptions > 0 ? 'underline' : 'none',
                                        }}
                                        title={c.nb_redemptions > 0 ? 'Voir qui a copié et quand' : undefined}
                                    >
                                        {c.nb_redemptions} copie{c.nb_redemptions > 1 ? 's' : ''}
                                    </button>
                                    <button type="button" className="sem-icon-btn" title="Copier le code" onClick={() => copyCode(c.code, c.id)}>
                                        {genCopiedId === c.id ? <Check size={13} style={{ color: '#10b981' }} /> : <Copy size={13} />}
                                    </button>
                                    <button type="button" className="sem-icon-btn"
                                        title={c.is_active ? 'Révoquer (les copies déjà faites restent)' : 'Réactiver'}
                                        onClick={() => handleRevokeThematiqueCode(c.id, !c.is_active)}>
                                        {c.is_active ? <PowerOff size={13} style={{ color: '#ef4444' }} /> : <Power size={13} style={{ color: '#10b981' }} />}
                                    </button>
                                </div>
                                {expandedCode === c.id && (c.redemptions || []).length > 0 && (
                                    <div style={{ borderTop: '1px solid var(--border)', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {c.redemptions.map((r, i) => (
                                            <div key={i} style={{ fontSize: '0.78em', color: 'var(--text-muted)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
                                                <strong style={{ color: 'var(--text)' }}>{r.prenom || r.email}</strong>
                                                {r.prenom && r.email && <span>{r.email}</span>}
                                                <span>· {fmtRedeemDate(r.redeemed_at)}</span>
                                                <span>· {r.nb_sujets_copied} sujet{r.nb_sujets_copied > 1 ? 's' : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
